'use client';

import type { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack, token } from '@terra/ui-styled-system';
import {
  Badge,
  BodyDefault,
  Checkbox,
  IconButton,
  Tooltip,
  getStatusIcon,
} from '@terra/ui-design-system';
import { Delete02Icon, StopIcon } from '@terra/ui-icons';
import type { AgentExecutionDto } from '@terra/shared-contracts-agent-execution';
import { useDateFormatter } from '../../../hooks/useDateFormatter';
import { rowClickBlockerProps } from '../rowClickBlocker';
import { EXECUTIONS_SELECT_ALL_STATE } from '../executions-table/executions-list.model';
import {
  formatExecutionDuration,
  isAbortableAgentExecution,
  resolveAgentExecutionTitle,
} from './agent-executions-list.model';
import { useAgentExecutionsTableContext } from './AgentExecutionsTableContext';

type AgentExecutionCellProps = {
  execution: AgentExecutionDto;
};

export function AgentExecutionObjectiveCell({
  execution,
}: AgentExecutionCellProps) {
  const t = useTranslations('agentExecutions');

  return (
    <BodyDefault truncate>
      {resolveAgentExecutionTitle(execution) ?? t('untitled')}
    </BodyDefault>
  );
}

export function AgentExecutionStatusCell({
  execution,
}: AgentExecutionCellProps) {
  const t = useTranslations('agentExecutions.status');

  return (
    <Badge
      variant="outline"
      size="sm"
      bg={token('colors.bg.neutral.default')}
      borderColor={token('colors.border.neutral.muted')}
      icon={getStatusIcon(execution.status)}
    >
      {t(execution.status)}
    </Badge>
  );
}

export function AgentExecutionDurationCell({
  execution,
}: AgentExecutionCellProps) {
  const duration = formatExecutionDuration(execution.duration);

  return duration ? (
    <BodyDefault color="fg.neutral.muted">{duration}</BodyDefault>
  ) : null;
}

export function AgentExecutionCreatedAtCell({
  execution,
}: AgentExecutionCellProps) {
  const dateFormatter = useDateFormatter();

  return (
    <BodyDefault color="fg.neutral.muted" truncate>
      {dateFormatter.format(new Date(execution.createdAt))}
    </BodyDefault>
  );
}

export function AgentExecutionActionsCell({
  execution,
}: AgentExecutionCellProps) {
  const t = useTranslations('agentExecutions');
  const {
    abortingIds,
    isSelectionLocked,
    onAbortExecution,
    onDeleteExecution,
  } = useAgentExecutionsTableContext();
  const isAborting = abortingIds.has(execution.id);

  return (
    <HStack gap={1} h="full" {...rowClickBlockerProps}>
      {isAbortableAgentExecution(execution) ? (
        <Tooltip title={t('abortTooltip')}>
          <IconButton
            variant="ghost"
            size="xs"
            disabled={isAborting || isSelectionLocked}
            onClick={() => onAbortExecution(execution)}
            aria-label={t('abortAriaLabel')}
          >
            <StopIcon size={14} />
          </IconButton>
        </Tooltip>
      ) : null}
      <Tooltip title={t('deleteTooltip')}>
        <IconButton
          variant="ghost"
          size="xs"
          disabled={isSelectionLocked}
          onClick={() => onDeleteExecution(execution)}
          aria-label={t('deleteAriaLabel')}
        >
          <Delete02Icon size={14} />
        </IconButton>
      </Tooltip>
    </HStack>
  );
}

function SelectionCheckboxFrame({ children }: { children: ReactNode }) {
  return (
    <Box
      w="full"
      h="full"
      display="flex"
      alignItems="center"
      justifyContent="center"
      {...rowClickBlockerProps}
    >
      {children}
    </Box>
  );
}

export function AgentExecutionsSelectAllHeader() {
  const t = useTranslations('proxy.executions.table');
  const { selectAllState, hasRows, isSelectionLocked, onSelectAll } =
    useAgentExecutionsTableContext();

  return (
    <SelectionCheckboxFrame>
      <Checkbox
        size="sm"
        aria-label={t('selectAll')}
        checked={
          selectAllState === EXECUTIONS_SELECT_ALL_STATE.ALL
            ? true
            : selectAllState === EXECUTIONS_SELECT_ALL_STATE.SOME
              ? 'indeterminate'
              : false
        }
        disabled={!hasRows || isSelectionLocked}
        onCheckedChange={(details) => onSelectAll(details.checked === true)}
      />
    </SelectionCheckboxFrame>
  );
}

export function AgentExecutionSelectionCell({
  execution,
}: AgentExecutionCellProps) {
  const t = useTranslations('proxy.executions.table');
  const { isExecutionSelected, isSelectionLocked, onToggleExecutionSelected } =
    useAgentExecutionsTableContext();

  return (
    <SelectionCheckboxFrame>
      <Checkbox
        size="sm"
        aria-label={t('selectExecution')}
        checked={isExecutionSelected(execution)}
        disabled={isSelectionLocked}
        onCheckedChange={(details) =>
          onToggleExecutionSelected({
            execution,
            selected: details.checked === true,
          })
        }
      />
    </SelectionCheckboxFrame>
  );
}
